import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Utensils, X, Search, Check, Edit3, Trash2, Plus } from 'lucide-react';
import { MealTimeSelector } from '../components/MealTimeSelector';

export function SavedMealsModal({
  isOpen,
  onClose,
  savedMeals,
  onAddMeal,
  onDeleteMeal,
  onRenameMeal,
  defaultMealTime = 'breakfast',
  mealTimes,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [editingMealId, setEditingMealId] = useState(null);
  const [editingMealName, setEditingMealName] = useState('');
  const [selectedMealTime, setSelectedMealTime] = useState(defaultMealTime);

  // Filter meals based on search
  const filteredMeals = useMemo(() => {
    const validMeals = savedMeals.filter(meal => 
      meal && typeof meal === 'object' && meal.id && typeof meal.name === 'string'
    );
    
    if (!searchQuery.trim()) return validMeals;
    
    const searchLower = searchQuery.toLowerCase();
    return validMeals.filter(meal => {
      const nameMatch = (meal.name || '').toLowerCase().includes(searchLower);
      const itemsMatch = (meal.items || meal.entries || []).some(item => 
        (item?.name || '').toLowerCase().includes(searchLower)
      );
      return nameMatch || itemsMatch;
    });
  }, [savedMeals, searchQuery]);

  const startEditingMeal = (meal) => {
    setEditingMealId(meal.id);
    setEditingMealName(meal.name || '');
  };

  const cancelEditing = () => {
    setEditingMealId(null);
    setEditingMealName('');
  };

  const handleRename = (mealId) => {
    if (editingMealName.trim()) {
      onRenameMeal(mealId, editingMealName.trim());
    }
    cancelEditing();
  };

  const handleClose = () => {
    setSearchQuery('');
    cancelEditing();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <Card className="bg-slate-800 border-slate-700 w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between shrink-0">
          <CardTitle className="text-white flex items-center gap-2">
            <Utensils className="h-5 w-5 text-orange-400" />
            Saved Meals
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-slate-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        
        {/* Search Bar */}
        <div className="px-6 pb-3 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search saved meals..."
              className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              data-testid="meal-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {/* Meal Time Selection */}
          <div className="space-y-1">
            <label className="text-sm text-slate-400">Add to:</label>
            <MealTimeSelector 
              selectedMealTime={selectedMealTime} 
              onMealTimeChange={setSelectedMealTime}
              compact
              mealTimes={mealTimes}
            />
          </div>
        </div>
        
        <CardContent className="overflow-y-auto flex-1">
          {filteredMeals.length > 0 ? (
            <div className="space-y-3">
              {filteredMeals.map(meal => {
                const mealItems = Array.isArray(meal.items) ? meal.items : 
                                 Array.isArray(meal.entries) ? meal.entries : [];
                const itemCount = mealItems.length;
                const totalCal = meal.totalCalories ?? mealItems.reduce((s, e) => s + (e?.calories || 0), 0);
                const totalP = meal.totalProtein ?? mealItems.reduce((s, e) => s + (e?.protein || 0), 0);
                const totalC = meal.totalCarbs ?? mealItems.reduce((s, e) => s + (e?.carbs || 0), 0);
                const totalF = meal.totalFat ?? mealItems.reduce((s, e) => s + (e?.fat || 0), 0);
                
                return (
                  <div 
                    key={meal.id}
                    className="p-4 bg-slate-700/50 rounded-lg"
                  >
                    {/* Header row */}
                    <div className="mb-3">
                      {editingMealId === meal.id ? (
                        <div className="flex items-center gap-2 mb-2">
                          <Input
                            value={editingMealName}
                            onChange={(e) => setEditingMealName(e.target.value)}
                            className="h-9 text-sm bg-slate-600 border-slate-500 flex-1"
                            placeholder="Enter meal name"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRename(meal.id);
                              else if (e.key === 'Escape') cancelEditing();
                            }}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRename(meal.id)}
                            className="h-9 w-9 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/20"
                          >
                            <Check className="h-5 w-5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={cancelEditing}
                            className="h-9 w-9 p-0 text-slate-400 hover:text-slate-300"
                          >
                            <X className="h-5 w-5" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-white font-medium text-base flex-1 mr-2">
                              {typeof meal.name === 'string' ? meal.name : 'Unnamed Meal'}
                            </p>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => startEditingMeal(meal)}
                              className="h-7 w-7 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">{itemCount} items</span>
                            <div className="text-right">
                              <span className="text-orange-400 font-medium">{Math.round(totalCal)} cal</span>
                              <span className="text-slate-400 ml-2">
                                P:{Math.round(totalP)}g C:{Math.round(totalC)}g F:{Math.round(totalF)}g
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => onAddMeal({ ...meal, mealTime: selectedMealTime })}
                        className="flex-1 bg-green-500 hover:bg-green-400"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add to Today
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDeleteMeal(meal.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Items preview */}
                    {mealItems.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-600">
                        <p className="text-xs text-slate-500 mb-1">Items:</p>
                        <div className="text-xs text-slate-400 space-y-0.5">
                          {mealItems.slice(0, 3).map((item, idx) => (
                            <p key={idx} className="truncate">
                              • {item?.name || 'Unknown'} ({item?.calories || 0} cal)
                            </p>
                          ))}
                          {mealItems.length > 3 && (
                            <p className="text-slate-500">+{mealItems.length - 3} more items</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Utensils className="h-12 w-12 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400">
                {searchQuery ? 'No meals found' : 'No saved meals yet'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Save meals from your food entries to reuse them
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SavedMealsModal;
